import { CRM_ADVISOR_INSIGHTS_ENABLED, CRM_ADVISOR_TOOLS_ENABLED } from '../config/featureFlags';
import { buildAdvisorDashboardSnapshot, getAtRiskDeals, buildContactDataQuality, buildCompanyDataQuality } from '../modules/crmAdvisorTools';
import { buildAdvisorPrompt, AdvisorInsightsData, AdvisorPromptOptions, AdvisorPromptBuild, AdvisorInsightsSummary } from '../modules/crmAdvisorPrompts';
import { fetchDashboardSummary } from './crm/dashboardService';
import dealsService from './crm/dealsService';
import contactsService from './crm/contactsService';
import companiesService from './crm/companiesService';
import type { Deal } from './crm/dealsService';
import type { Contact } from './crm/contactsService';
import type { Company } from './crm/companiesService';
import type { ToolResultAction } from '../types/businessConsultant';

export interface GenerateAdvisorInsightsParams {
  organizationId: string;
  consultantName: string;
  consultantTitle: string;
  businessName?: string | null;
  timeframeLabel?: string;
  timezone?: string;
  rangeDays?: 7 | 30 | 90;
  onlyMine?: boolean;
  includeDealsLimit?: number;
  includeContactsLimit?: number;
  includeCompaniesLimit?: number;
}

export interface AdvisorInsightsResult {
  prompt: AdvisorPromptBuild;
  data: AdvisorInsightsData;
  summary: AdvisorInsightsSummary;
  markdown: string;
  generatedAt: string;
  actions?: ToolResultAction[];
  source?: string;
  confidence?: number;
}

const DEFAULT_DEAL_LIMIT = 100;
const DEFAULT_CONTACT_LIMIT = 100;
const DEFAULT_COMPANY_LIMIT = 100;

function buildAdvisorActions(summary: AdvisorInsightsSummary): ToolResultAction[] {
  const actions: ToolResultAction[] = [];
  if (summary.atRiskDealCount > 0) {
    actions.push({
      type: 'send_message',
      label: 'Draft at-risk follow-ups',
      message: 'Draft follow-up emails for the top 3 at-risk deals with clear next steps.'
    });
  }

  if (summary.coverageRatio !== undefined && summary.coverageRatio < 3.0) {
    actions.push({
      type: 'navigate',
      label: 'Open Telemarketing',
      route: '/crm/telemarketing'
    });
    actions.push({
      type: 'send_message',
      label: 'Plan a pipeline boost',
      message: 'Design a 2-week pipeline acceleration plan with outreach, content, and partner tactics.'
    });
  }

  if ((summary.contactQualityAverage ?? 100) < 70 || (summary.companyQualityAverage ?? 100) < 70) {
    actions.push({
      type: 'navigate',
      label: 'Review CRM data',
      route: '/crm'
    });
    actions.push({
      type: 'send_message',
      label: 'Create hygiene checklist',
      message: 'Build a data hygiene checklist and assign owners for missing CRM fields.'
    });
  }

  if (!actions.length) {
    actions.push({
      type: 'send_message',
      label: 'Build a weekly plan',
      message: 'Turn these insights into a prioritized weekly execution plan with owners and deadlines.'
    });
  }

  return actions;
}

function inferAdvisorConfidence(data: AdvisorInsightsData): number {
  let score = 0.4;
  if (data.snapshot) score += 0.2;
  if (data.atRiskDeals && data.atRiskDeals.length > 0) score += 0.15;
  if (data.dataQuality?.contacts?.length) score += 0.15;
  if (data.dataQuality?.companies?.length) score += 0.1;
  return Math.min(0.85, Math.max(0.3, score));
}

export async function generateAdvisorInsights(params: GenerateAdvisorInsightsParams): Promise<AdvisorInsightsResult> {
  if (!CRM_ADVISOR_TOOLS_ENABLED || !CRM_ADVISOR_INSIGHTS_ENABLED) {
    throw new Error('Advisor insights feature is disabled.');
  }

  const {
    organizationId,
    consultantName,
    consultantTitle,
    businessName,
    timeframeLabel,
    timezone,
    rangeDays = 30,
    onlyMine = false,
    includeDealsLimit = DEFAULT_DEAL_LIMIT,
    includeContactsLimit = DEFAULT_CONTACT_LIMIT,
    includeCompaniesLimit = DEFAULT_COMPANY_LIMIT,
  } = params;

  if (!organizationId) {
    throw new Error('organizationId is required to generate advisor insights.');
  }

  let dashboardSummary = null;
  try {
    dashboardSummary = await fetchDashboardSummary(organizationId, { rangeDays, onlyMine });
  } catch (err) {
    console.warn('Unable to load dashboard summary for advisor insights:', err);
  }

  let deals: Deal[] = [];
  try {
    const { data } = await dealsService.list({ organization_id: organizationId, limit: includeDealsLimit });
    deals = data || [];
  } catch (err) {
    console.warn('Unable to load deals for advisor insights:', err);
  }

  let contacts: Contact[] = [];
  try {
    const { data } = await contactsService.list({ organization_id: organizationId, limit: includeContactsLimit });
    contacts = data || [];
  } catch (err) {
    console.warn('Unable to load contacts for advisor insights:', err);
  }

  let companies: Company[] = [];
  try {
    const { data } = await companiesService.list({ organization_id: organizationId, limit: includeCompaniesLimit });
    companies = data || [];
  } catch (err) {
    console.warn('Unable to load companies for advisor insights:', err);
  }

  const data: AdvisorInsightsData = {
    snapshot: buildAdvisorDashboardSnapshot(dashboardSummary),
    atRiskDeals: getAtRiskDeals(deals, { probabilityThreshold: 0.45, staleDays: 14 }),
    dataQuality: {
      contacts: buildContactDataQuality(contacts),
      companies: buildCompanyDataQuality(companies),
    }
  };

  const promptOptions: AdvisorPromptOptions = {
    consultantName,
    consultantTitle,
    organizationId,
    businessName: businessName || undefined,
    timeframeLabel,
    timezone,
  };

  const prompt = buildAdvisorPrompt(data, promptOptions);
  const actions = buildAdvisorActions(prompt.summary);
  const confidence = inferAdvisorConfidence(data);

  return {
    prompt,
    data,
    summary: prompt.summary,
    markdown: prompt.fallback,
    generatedAt: new Date().toISOString(),
    actions,
    source: 'CRM',
    confidence,
  };
}
